import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Pencil } from 'lucide-react';
import SprekersLayout from '@/components/SprekersLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/context/AuthContext';
import { sprekersApi } from '../api';
import SectionHeader from '../components/SectionHeader';
import ProfileFormDialog from '../components/ProfileFormDialog';
import { AVAILABILITY_STATUSES, findClassName, findLabel } from '../labels';
import type { SpeakerProfile, SpeakerProfileInput } from '../types';

export default function ProfilesPage() {
  const { user, hasCapability } = useAuth();
  const canManage = hasCapability('speakers_manage');

  const [profiles, setProfiles] = useState<SpeakerProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProfile, setEditingProfile] = useState<SpeakerProfile | null>(null);

  const fetchProfiles = async () => {
    try {
      const data = await sprekersApi.listProfiles();
      setProfiles(data);
    } catch (error) {
      toast.error('Kon sprekersprofielen niet laden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfiles();
  }, []);

  const openCreate = () => {
    setEditingProfile(null);
    setDialogOpen(true);
  };

  const openEdit = (profile: SpeakerProfile) => {
    setEditingProfile(profile);
    setDialogOpen(true);
  };

  const handleSubmit = async (data: SpeakerProfileInput) => {
    try {
      if (editingProfile) {
        await sprekersApi.updateProfile(editingProfile.id, data);
        toast.success('Sprekersprofiel bijgewerkt');
      } else {
        await sprekersApi.createProfile(data);
        toast.success('Sprekersprofiel aangemaakt');
      }
      fetchProfiles();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Opslaan mislukt');
      throw error;
    }
  };

  return (
    <SprekersLayout>
      <SectionHeader
        title="Sprekersprofielen"
        description="Beheer sprekers, hun beschikbaarheid en voorkeuren."
        action={canManage && (
          <Button onClick={openCreate}>
            <Plus className="mr-2 h-4 w-4" />
            Nieuw sprekersprofiel
          </Button>
        )}
      />

      <Card>
        <CardContent className="p-0">
          {!loading && profiles.length === 0 && (
            <p className="p-6 text-sm text-slate-500">Nog geen sprekersprofielen.</p>
          )}
          <div className="divide-y divide-slate-100">
            {profiles.map((profile) => {
              const canEdit = canManage || profile.user_id === user?.id;
              return (
                <div key={profile.id} className="flex items-center justify-between gap-4 p-4">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-medium text-slate-900">{profile.display_name}</p>
                      <Badge className={findClassName(AVAILABILITY_STATUSES, profile.availability_status)}>
                        {findLabel(AVAILABILITY_STATUSES, profile.availability_status)}
                      </Badge>
                      {!profile.is_active && <Badge className="bg-slate-100 text-slate-400">Inactief</Badge>}
                    </div>
                    {profile.biography && <p className="mt-1 text-sm text-slate-500">{profile.biography}</p>}
                  </div>
                  {canEdit && (
                    <Button variant="ghost" size="icon" onClick={() => openEdit(profile)} aria-label="Bewerken">
                      <Pencil className="h-4 w-4 text-slate-400" />
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <ProfileFormDialog open={dialogOpen} onOpenChange={setDialogOpen} profile={editingProfile} onSubmit={handleSubmit} />
    </SprekersLayout>
  );
}
